/**
 * /accounting/ledger/$accountId — the account ledger (2026-10 persona
 * round): opening balance, every journal line with its voucher number,
 * source document, narration, debit / credit and running balance, and the
 * closing balance. Every amount on the Trial Balance, Profit and Loss and
 * Balance Sheet opens this page; each line links to its journal and its
 * source document. CSV export of the same rows.
 */

import { useState } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { z } from "zod";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/format";
import { toApiError } from "@/lib/api";
import { downloadAccountLedgerCSV } from "@/lib/api/accounting";
import { saveBlobAs } from "@/lib/save-blob";
import { useAccountLedger } from "@/hooks/use-accounting";
import { formatAccountsAmount, formatDrCr, journalSourceRoute } from "@/lib/accounting-gst";
import { useSmPlus } from "@/lib/use-sm-plus";

const ledgerSearchSchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/accounting/ledger/$accountId")({
  validateSearch: ledgerSearchSchema,
  component: AccountLedgerRoute,
});

function AccountLedgerRoute() {
  const { tenantSlug, accountId } = useParams({ strict: false }) as {
    tenantSlug: string;
    accountId: string;
  };
  const search = Route.useSearch();
  const navigate = useNavigate();
  const id = Number(accountId);
  const q = useAccountLedger(id, { from: search.from, to: search.to });
  const data = q.data;
  const isSmPlus = useSmPlus();
  const [pendingFrom, setPendingFrom] = useState<string>(search.from ?? "");
  const [pendingTo, setPendingTo] = useState<string>(search.to ?? "");
  const [downloading, setDownloading] = useState(false);

  function apply() {
    void navigate({
      to: "/app/$tenantSlug/accounting/ledger/$accountId",
      params: { tenantSlug, accountId },
      search: { from: pendingFrom || undefined, to: pendingTo || undefined },
    });
  }

  async function downloadCSV() {
    setDownloading(true);
    try {
      const blob = await downloadAccountLedgerCSV(id, { from: data?.from ?? search.from, to: data?.to ?? search.to });
      saveBlobAs(blob, `ledger-${data?.account_code ?? id}.csv`);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <AccountingModuleShell
      title={data ? `Ledger — ${data.account_code} ${data.account_name}` : "Ledger"}
      description={
        data
          ? `${formatDate(data.from)} to ${formatDate(data.to)}. Click a voucher to open the journal, or the document to open it.`
          : "Every entry on this account, with the running balance."
      }
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Reports
            </Link>
          </Button>
          <Button type="button" className="gap-1.5" onClick={() => void downloadCSV()} disabled={!data || downloading}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-[1fr_1fr_auto]">
          <div className="grid gap-1.5">
            <Label htmlFor="ledger-from">From</Label>
            <Input
              id="ledger-from"
              type="date"
              value={pendingFrom || data?.from || ""}
              onChange={(e) => setPendingFrom(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ledger-to">To</Label>
            <Input
              id="ledger-to"
              type="date"
              value={pendingTo || data?.to || ""}
              onChange={(e) => setPendingTo(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button type="button" onClick={apply}>
              Show
            </Button>
          </div>
        </CardContent>
      </Card>

      {q.isError && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load the ledger</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.isPending ? (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : data ? (
        <div className="mt-4 space-y-3">
          {data.truncated && (
            <Alert>
              <AlertTitle>Showing the first 5,000 entries</AlertTitle>
              <AlertDescription>Pick a shorter date range to see the rest.</AlertDescription>
            </Alert>
          )}
          {/* Phones: one row per entry — document / narration with the
              Dr or Cr amount on the right, then date · voucher and the
              running balance underneath; opening and closing as summary
              rows. */}
          {!isSmPlus && (
          <div data-testid="ledger-cards">
            <ul className="divide-y rounded-md border">
              <li className="flex items-baseline justify-between gap-3 bg-muted/40 px-3 py-2.5 text-sm">
                <span className="min-w-0">
                  <span className="font-medium">Opening balance</span>
                  <span className="block text-xs text-muted-foreground">
                    {formatDate(data.from)}
                  </span>
                </span>
                <span className="shrink-0 whitespace-nowrap tabular-nums">
                  {formatDrCr(data.opening_net_minor)}
                </span>
              </li>
              {data.lines.length === 0 ? (
                <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No entries on this account in these dates.
                </li>
              ) : (
                data.lines.map((l, i) => {
                  const doc = journalSourceRoute(l.source);
                  const note = [
                    l.narration,
                    l.description && l.description !== l.narration ? l.description : "",
                    l.contact_name,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <li key={`${l.entry_id}-${i}`} className="space-y-1 px-3 py-2.5 text-sm">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="min-w-0 break-words">
                          {l.source ? (
                            doc ? (
                              <Link
                                to={doc.to}
                                params={{ tenantSlug, id: doc.id }}
                                className="font-medium text-primary underline-offset-4 hover:underline"
                              >
                                {l.source.label}
                              </Link>
                            ) : (
                              <span className="font-medium">{l.source.label}</span>
                            )
                          ) : (
                            <span className="text-gray-900">{note || "—"}</span>
                          )}
                        </div>
                        <span className="shrink-0 whitespace-nowrap tabular-nums">
                          {l.debit_minor ? (
                            <>
                              {formatAccountsAmount(l.debit_minor)}{" "}
                              <span className="text-xs text-muted-foreground">Dr</span>
                            </>
                          ) : l.credit_minor ? (
                            <>
                              {formatAccountsAmount(l.credit_minor)}{" "}
                              <span className="text-xs text-muted-foreground">Cr</span>
                            </>
                          ) : (
                            "—"
                          )}
                        </span>
                      </div>
                      {l.source && note && (
                        <p className="break-words text-xs text-muted-foreground">{note}</p>
                      )}
                      <div className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
                        <span className="flex flex-wrap items-center gap-x-3">
                          <span className="whitespace-nowrap">{formatDate(l.date)}</span>
                          <Link
                            to="/app/$tenantSlug/accounting/journals/$id"
                            params={{ tenantSlug, id: String(l.entry_id) }}
                            className="-mx-1 inline-flex min-h-6 items-center px-1 font-mono text-primary underline-offset-4 hover:underline"
                          >
                            #{l.entry_no}
                          </Link>
                        </span>
                        <span className="shrink-0 whitespace-nowrap tabular-nums">
                          Balance {formatDrCr(l.running_net_minor)}
                        </span>
                      </div>
                    </li>
                  );
                })
              )}
              <li className="space-y-1 bg-muted/40 px-3 py-2.5 text-sm">
                <div className="flex items-baseline justify-between gap-3 font-semibold">
                  <span>Closing balance</span>
                  <span className="whitespace-nowrap tabular-nums">
                    {formatDrCr(data.closing_net_minor)}
                  </span>
                </div>
                <div className="flex flex-wrap justify-between gap-x-3 text-xs tabular-nums text-muted-foreground">
                  <span>{formatDate(data.to)}</span>
                  <span className="whitespace-nowrap">
                    Dr {formatAccountsAmount(data.total_debit_minor)} · Cr{" "}
                    {formatAccountsAmount(data.total_credit_minor)}
                  </span>
                </div>
              </li>
            </ul>
          </div>
          )}
          {isSmPlus && (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Date</TableHead>
                  <TableHead className="w-24">Voucher</TableHead>
                  <TableHead>Document / Narration</TableHead>
                  <TableHead className="w-36 text-right">Debit</TableHead>
                  <TableHead className="w-36 text-right">Credit</TableHead>
                  <TableHead className="w-40 text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-muted/40">
                  <TableCell>{formatDate(data.from)}</TableCell>
                  <TableCell />
                  <TableCell className="font-medium">Opening balance</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-right tabular-nums">{formatDrCr(data.opening_net_minor)}</TableCell>
                </TableRow>
                {data.lines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                      No entries on this account in these dates.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.lines.map((l, i) => {
                    const doc = journalSourceRoute(l.source);
                    return (
                      <TableRow key={`${l.entry_id}-${i}`}>
                        <TableCell className="whitespace-nowrap">{formatDate(l.date)}</TableCell>
                        <TableCell>
                          <Link
                            to="/app/$tenantSlug/accounting/journals/$id"
                            params={{ tenantSlug, id: String(l.entry_id) }}
                            className="font-mono text-xs text-primary underline-offset-4 hover:underline"
                          >
                            #{l.entry_no}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {l.source &&
                            (doc ? (
                              <Link
                                to={doc.to}
                                params={{ tenantSlug, id: doc.id }}
                                className="font-medium text-primary underline-offset-4 hover:underline"
                              >
                                {l.source.label}
                              </Link>
                            ) : (
                              <span className="font-medium">{l.source.label}</span>
                            ))}
                          <span className="block text-xs text-muted-foreground">
                            {[l.narration, l.description && l.description !== l.narration ? l.description : "", l.contact_name]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {l.debit_minor ? formatAccountsAmount(l.debit_minor) : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {l.credit_minor ? formatAccountsAmount(l.credit_minor) : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatDrCr(l.running_net_minor)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell>{formatDate(data.to)}</TableCell>
                  <TableCell />
                  <TableCell>Closing balance</TableCell>
                  <TableCell className="text-right tabular-nums">{formatAccountsAmount(data.total_debit_minor)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatAccountsAmount(data.total_credit_minor)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatDrCr(data.closing_net_minor)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          )}
        </div>
      ) : null}
    </AccountingModuleShell>
  );
}
